import React, { useEffect, useState } from 'react';
import {
  Building,
  CreditCard,
  DollarSign,
  CheckCircle2,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Send,
  Shield,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';
import type { AdminOrganizationDto, AdminUserDto } from '@antigravity/types';

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [orgs, setOrgs] = useState<AdminOrganizationDto[]>([]);
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'orgs' | 'users'>('orgs');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [statsData, orgsData, usersData] = await Promise.all([
        apiClient.getAdminStats(),
        apiClient.getAdminOrganizations(1, 50, search || undefined),
        apiClient.getAdminUsers(1, 50),
      ]);
      setStats(statsData);
      setOrgs(orgsData.items);
      setUsers(usersData.items);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load platform admin metrics' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isSuperAdmin) {
      loadAdminData();
    }
  }, [user]);

  const handleToggleOrgStatus = async (orgId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    if (!window.confirm(`Are you sure you want to change organization status to ${newStatus}?`)) {
      return;
    }
    try {
      await apiClient.updateAdminOrgStatus(orgId, newStatus as any, 'Admin override via dashboard');
      setMessage({ type: 'success', text: `Organization status set to ${newStatus}` });
      await loadAdminData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Status update failed' });
    }
  };

  const handleAssignPlan = async (orgId: string, planCode: string) => {
    try {
      await apiClient.assignAdminOrgPlan(orgId, planCode);
      setMessage({ type: 'success', text: `Assigned ${planCode} plan to organization` });
      await loadAdminData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Plan assignment failed' });
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    if (!window.confirm(`Are you sure you want to change user status to ${newStatus}?`)) {
      return;
    }
    try {
      await apiClient.updateAdminUserStatus(userId, newStatus as any, 'Admin override');
      setMessage({ type: 'success', text: `User status set to ${newStatus}` });
      await loadAdminData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'User status update failed' });
    }
  };

  if (!user?.isSuperAdmin) {
    return (
      <div className="p-12 rounded-2xl bg-red-950/20 border border-red-500/20 text-center space-y-3">
        <ShieldAlert className="w-12 h-12 text-red-400 mx-auto" />
        <h2 className="text-xl font-bold text-white">Super-Admin Access Required</h2>
        <p className="text-xs text-slate-400 max-w-md mx-auto">
          This portal is restricted to authorized platform operators. Your account does not have super-admin privileges.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[11px] font-bold mb-2">
            <Shield className="w-3.5 h-3.5" />
            <span>Super-Admin Operator Console</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Platform Administration</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Global tenant oversight, plan assignment, user governance, and infrastructure capacity monitoring.
          </p>
        </div>
        <button
          onClick={loadAdminData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 text-xs font-semibold text-slate-300 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Alert Banner */}
      {message && (
        <div
          className={`p-4 rounded-xl text-sm border flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-300'
              : 'bg-red-950/40 border-red-500/30 text-red-300'
          }`}
        >
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="text-xs font-mono opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* KPI Stats Grid */}
      {stats && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <Building className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-semibold">Total Tenants</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.totalOrganizations}</p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-semibold">Active Subscriptions</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">{stats.totalActiveSubscriptions}</p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <Users className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-semibold">Registered Users</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <Mail className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold">Active Mailboxes</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.totalMailboxes}</p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <Send className="w-4 h-4 text-brand-400" />
                <span className="text-xs font-semibold">Total Campaigns</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.totalCampaigns}</p>
            </div>
          </div>

          {/* Secondary Revenue & Deliverability Telemetry */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-900/80 to-emerald-950/20 border border-emerald-500/20 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">Monthly Recurring Revenue</div>
                <div className="text-xl font-extrabold text-white mt-0.5">
                  ${((stats.monthlyRecurringRevenueCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <DollarSign className="w-7 h-7 text-emerald-400/50" />
            </div>

            <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-900/80 to-blue-950/20 border border-blue-500/20 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">Total Emails Dispatched</div>
                <div className="text-xl font-extrabold text-white mt-0.5">
                  {(stats.totalEmailsSent || 0).toLocaleString()}
                </div>
              </div>
              <Send className="w-7 h-7 text-blue-400/50" />
            </div>

            <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-900/80 to-purple-950/20 border border-purple-500/20 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold text-purple-400 uppercase tracking-wider">Successful Deliveries</div>
                <div className="text-xl font-extrabold text-white mt-0.5">
                  {(stats.totalDelivered || 0).toLocaleString()} <span className="text-xs text-slate-400 font-normal">({stats.totalEmailsSent ? Math.round(((stats.totalDelivered || 0) / stats.totalEmailsSent) * 100) : 100}%)</span>
                </div>
              </div>
              <CheckCircle2 className="w-7 h-7 text-purple-400/50" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('orgs')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
              activeTab === 'orgs'
                ? 'bg-brand-600/15 text-brand-300 border border-brand-500/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Organizations ({orgs.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
              activeTab === 'users'
                ? 'bg-brand-600/15 text-brand-300 border border-brand-500/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Users ({users.length})
          </button>
        </div>

        {activeTab === 'orgs' && (
          <div className="relative w-full sm:w-auto">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadAdminData()}
              className="pl-8 pr-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 w-full sm:w-64"
            />
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin text-brand-400 mb-3" />
          <p className="text-sm">Loading platform data...</p>
        </div>
      ) : activeTab === 'orgs' ? (
        <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900/40">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[750px] text-left text-xs text-slate-300">
              <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
                <tr>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Plan / Status</th>
                  <th className="px-4 py-3">Resources</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {orgs.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-white">{o.name}</p>
                      <p className="font-mono text-[10px] text-slate-400">{o.slug}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white">
                        {o.owner.firstName} {o.owner.lastName}
                      </p>
                      <p className="font-mono text-[10px] text-slate-400">{o.owner.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={o.planCode}
                          onChange={(e) => handleAssignPlan(o.id, e.target.value)}
                          className="px-2 py-1 rounded bg-slate-950 border border-slate-800 text-[11px] font-semibold text-brand-300"
                        >
                          <option value="FREE">FREE</option>
                          <option value="STARTER">STARTER</option>
                          <option value="PRO">PRO</option>
                          <option value="ENTERPRISE">ENTERPRISE</option>
                        </select>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                          o.subscriptionStatus === 'ACTIVE'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : o.subscriptionStatus === 'TRIALING'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {o.subscriptionStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[11px]">
                        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                          {o.mailboxCount} mailboxes
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">
                          {o.domainCount} domains
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-300 border border-brand-500/20">
                          {o.campaignCount ?? 0} campaigns
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                          {o.memberCount} members
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(o.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleToggleOrgStatus(o.id, o.status)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                          o.status === 'ACTIVE'
                            ? 'text-red-400 hover:bg-red-500/10 border border-red-500/20'
                            : 'text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/20'
                        }`}
                      >
                        {o.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900/40">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[650px] text-left text-xs text-slate-300">
              <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role Status</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3 font-semibold text-white">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            u.status === 'ACTIVE'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : 'bg-red-500/10 text-red-400 border border-red-500/20'
                          }`}
                        >
                          {u.status}
                        </span>
                        {u.isSuperAdmin && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                            SUPER-ADMIN
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleToggleUserStatus(u.id, u.status)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                          u.status === 'ACTIVE'
                            ? 'text-red-400 hover:bg-red-500/10 border border-red-500/20'
                            : 'text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/20'
                        }`}
                      >
                        {u.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
